import { useState, useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import styles from "./PDFViewer.module.css";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const isMobileDevice = () => window.innerWidth < 768;

// ── Audio Player Component ────────────────────────────────────────────────────
function AudioPlayer({ courseTitle, userName, audioUrl, onClose }) {
  const audioRef              = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [minimized, setMinimized]     = useState(false);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");

  // Media Session API — lock screen / notification controls
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title:  courseTitle,
      artist: "Shortly Life",
      album:  courseTitle,
    });
    navigator.mediaSession.setActionHandler("play",  () => { audioRef.current?.play(); setPlaying(true); });
    navigator.mediaSession.setActionHandler("pause", () => { audioRef.current?.pause(); setPlaying(false); });
  }, [courseTitle]);

  // Load audio when URL is ready
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;
    setLoading(true);
    setError("");
    setCurrentTime(0);
    setDuration(0);
    setPlaying(false);
    const audio   = audioRef.current;
    audio.src     = audioUrl;
    audio.preload = "auto";
    audio.load();
  }, [audioUrl]);

  const handleLoaded     = () => { setLoading(false); setDuration(audioRef.current?.duration || 0); };
  const handleError      = () => { setLoading(false); setError("Audio unavailable. Please try again."); };
  const handleTimeUpdate = () => setCurrentTime(audioRef.current?.currentTime || 0);
  const handleEnded      = () => setPlaying(false);

  const togglePlay = () => {
    if (!audioRef.current || loading) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect  = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = ratio * duration;
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m   = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {/* ── Single persistent <audio> — never unmounts, survives minimize ── */}
      <audio
        ref={audioRef}
        onLoadedData={handleLoaded}
        onError={handleError}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        style={{ display: "none" }}
      />

      {/* ── Minimized bar ── */}
      {minimized && (
        <div className={styles.miniPlayer}>
          <div className={styles.miniLeft}>
            <button className={styles.miniPlay} onClick={togglePlay}>
              {playing ? "⏸" : "▶"}
            </button>
            <div>
              <p className={styles.miniTitle}>{courseTitle}</p>
              <p className={styles.miniMeta}>{fmt(currentTime)} / {fmt(duration)}</p>
            </div>
          </div>
          <div className={styles.miniRight}>
            <button className={styles.miniExpand} onClick={() => setMinimized(false)}>↑</button>
            <button className={styles.miniClose} onClick={onClose}>✕</button>
          </div>
          <div className={styles.miniProgress}>
            <div className={styles.miniProgressFill} style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* ── Full player ── */}
      {!minimized && (
        <div className={styles.playerBackdrop}>
          <div className={styles.player}>
            <div className={styles.playerHandle} />

            <div className={styles.playerHeader}>
              <button className={styles.playerMinimize} onClick={() => setMinimized(true)}>↓ Minimize</button>
              <button className={styles.playerClose} onClick={onClose}>✕</button>
            </div>

            <div className={styles.playerArt}>
              <span>🎧</span>
            </div>

            <div className={styles.playerInfo}>
              <p className={styles.playerTitle}>{courseTitle}</p>
              <p className={styles.playerMeta}>Full Audio Narration</p>
              <p className={styles.playerUser}>{userName}</p>
            </div>

            {error && <p className={styles.playerError}>{error}</p>}

            <div className={styles.progressWrap} onClick={seek}>
              <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${progress}%` }}>
                  <div className={styles.progressThumb} />
                </div>
              </div>
              <div className={styles.progressTimes}>
                <span>{fmt(currentTime)}</span>
                <span>{fmt(duration)}</span>
              </div>
            </div>

            <div className={styles.playerControls}>
              <button
                className={styles.playerBtn}
                onClick={() => { if (audioRef.current) audioRef.current.currentTime -= 10; }}
                title="Back 10s"
              >⏪</button>

              <button
                className={[styles.playerBtn, styles.playerBtnMain].join(" ")}
                onClick={togglePlay}
                disabled={loading}
              >
                {loading
                  ? <span className={styles.playerSpinner} />
                  : (playing ? "⏸" : "▶")}
              </button>

              <button
                className={styles.playerBtn}
                onClick={() => { if (audioRef.current) audioRef.current.currentTime += 10; }}
                title="Forward 10s"
              >⏩</button>
            </div>

            <div className={styles.playerSpeed}>
              {[0.75, 1, 1.25, 1.5, 2].map((s) => (
                <button
                  key={s}
                  className={[
                    styles.speedBtn,
                    audioRef.current?.playbackRate === s ? styles.speedBtnActive : "",
                  ].join(" ")}
                  onClick={() => { if (audioRef.current) audioRef.current.playbackRate = s; }}
                >
                  {s}×
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Main PDFViewer ────────────────────────────────────────────────────────────
export default function PDFViewer({ course, user, onClose }) {
  const canvasRef = useRef(null);

  const [pdf, setPdf]           = useState(null);
  const [pageNum, setPageNum]   = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [zoom, setZoom]         = useState(1);

  const [showPlayer, setShowPlayer] = useState(false);
  const [audioUrl, setAudioUrl]     = useState("");

  const isMobile = isMobileDevice();

  // ── Load PDF ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const loadPDF = async () => {
      try {
        setLoading(true); setError("");
        if (!course.driveFileId || course.driveFileId === "YOUR_GOOGLE_DRIVE_FILE_ID") {
          throw new Error("PDF not yet available. Please contact support.");
        }
        const res = await fetch("/.netlify/functions/get-pdf", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fileId: course.driveFileId }),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.error || "Failed to load PDF.");
        }
        const { pdf: base64 } = await res.json();
        const binary = atob(base64);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const loaded = await pdfjsLib.getDocument({
          data: bytes,
          password: course.password,
          standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`,
        }).promise;
        if (cancelled) return;
        setPdf(loaded);
        setNumPages(loaded.numPages);
        setLoading(false);
      } catch (err) {
        if (!cancelled) { setError(err.message || "Failed to load PDF"); setLoading(false); }
      }
    };
    loadPDF();
    return () => { cancelled = true; };
  }, [course]);

  // ── Render page ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;

    const render = async () => {
      try {
        const page   = await pdf.getPage(pageNum);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas)  return;
        const ctx    = canvas.getContext("2d");

        canvas.style.width  = "";
        canvas.style.height = "";
        canvas.width  = 0;
        canvas.height = 0;

        const dpr       = window.devicePixelRatio || 1;
        const viewport0 = page.getViewport({ scale: 1 });

        let scale;
        if (isMobile) {
          const displayW = window.innerWidth;
          scale = (displayW / viewport0.width) * dpr;
          canvas.style.width  = `${displayW}px`;
          canvas.style.height = `${(viewport0.height / viewport0.width) * displayW}px`;
        } else {
          const wrap   = canvas.closest(`.${styles.viewerWrap}`) || canvas.parentElement;
          const availW = Math.min(wrap.clientWidth - 48, 1100);
          scale = (availW / viewport0.width) * dpr * zoom;
          const vp = page.getViewport({ scale });
          canvas.style.width  = `${vp.width  / dpr}px`;
          canvas.style.height = `${vp.height / dpr}px`;
        }

        const viewport = page.getViewport({ scale });
        canvas.width   = viewport.width;
        canvas.height  = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch (err) { console.error("Render error:", err); }
    };

    render();
    return () => { cancelled = true; };
  }, [pdf, pageNum, zoom, isMobile]);

  // ── Open audio player ─────────────────────────────────────────────────────
  const handleOpenPlayer = async () => {
    setShowPlayer(true);
    if (audioUrl) return; // already loaded

    if (!course.audioUrl && !course.audioFileId) {
      console.warn("No audio configured for this course.");
      return;
    }

    // Cloudflare R2 direct URL
    if (course.audioUrl) {
      setAudioUrl(course.audioUrl);
      return;
    }

    // Fallback: signed Drive URL
    try {
      const res = await fetch("/.netlify/functions/get-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileId: course.audioFileId, isAudio: true }),
      });
      if (!res.ok) throw new Error("Failed to fetch audio.");
      const { signedUrl } = await res.json();
      setAudioUrl(signedUrl);
    } catch (err) {
      console.warn("Audio fetch failed:", err.message);
    }
  };

  // ── Anti-copy ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const no = (e) => e.preventDefault();
    document.addEventListener("contextmenu", no);
    document.addEventListener("copy", no);
    return () => {
      document.removeEventListener("contextmenu", no);
      document.removeEventListener("copy", no);
    };
  }, []);

  const prevPage = () => setPageNum((p) => Math.max(1, p - 1));
  const nextPage = () => setPageNum((p) => Math.min(numPages, p + 1));

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight") nextPage();
      if (e.key === "ArrowLeft")  prevPage();
      if (e.key === "Escape")     onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line
  }, [numPages, onClose]);

  return (
    <div className={[styles.backdrop, isMobile ? styles.mobile : ""].join(" ")}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
          <div className={styles.headerText}>
            <p className={styles.headerTitle}>{course.courseTitle}</p>
            <p className={styles.headerMeta}>
              {loading ? "Loading…" : `Page ${pageNum} of ${numPages}`}
            </p>
          </div>
        </div>
        {!loading && !error && (
          <button className={styles.listenBtn} onClick={handleOpenPlayer}>
            🎧 {isMobile ? "Listen" : "Listen to this page"}
          </button>
        )}
      </div>

      {/* PDF Viewer */}
      <div className={styles.viewerWrap} onContextMenu={(e) => e.preventDefault()}>
        {loading && (
          <div className={styles.statusBox}>
            <div className={styles.spinnerLg} />
            <p>Loading your course…</p>
          </div>
        )}
        {error && (
          <div className={styles.statusBox}>
            <span style={{ fontSize: 48 }}>⚠️</span>
            <p style={{ color: "var(--red)" }}>{error}</p>
            <p style={{ fontSize: 13, color: "var(--text2)", marginTop: 8 }}>
              Need help? Email support@shortly-life.in
            </p>
          </div>
        )}
        {!loading && !error && (
          <div className={styles.canvasWrap}>
            <canvas ref={canvasRef} className={styles.canvas} />
            <div className={styles.watermark}>
              {user.email || user.phone} · {course.orderId}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!loading && !error && (
        <div className={styles.footer}>
          <button className={styles.navBtn} onClick={prevPage} disabled={pageNum <= 1}>
            ← {!isMobile && "Previous"}
          </button>

          {!isMobile && (
            <div className={styles.zoomControls}>
              <button className={styles.zoomBtn} onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} disabled={zoom <= 0.5}>−</button>
              <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
              <button className={styles.zoomBtn} onClick={() => setZoom((z) => Math.min(3, z + 0.25))} disabled={zoom >= 3}>+</button>
            </div>
          )}

          <div className={styles.pageJump}>
            <input
              type="number"
              min={1} max={numPages}
              value={pageNum}
              onChange={(e) => setPageNum(Math.max(1, Math.min(numPages, Number(e.target.value) || 1)))}
              className={styles.pageInput}
            />
            <span>/ {numPages}</span>
          </div>

          <button className={styles.navBtn} onClick={nextPage} disabled={pageNum >= numPages}>
            {!isMobile && "Next"} →
          </button>
        </div>
      )}

      {/* Audio Player — single instance, persists through minimize */}
      {showPlayer && (
        <AudioPlayer
          courseTitle={course.courseTitle}
          userName={user.email || user.phone}
          audioUrl={audioUrl}
          onClose={() => { setShowPlayer(false); setAudioUrl(""); }}
        />
      )}
    </div>
  );
}